package org.example.project.enginepersonal

import org.example.project.settings.UISettings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.manager.LumaticaApiManager
import org.example.project.screens_cus.UrlaubsApi
import org.example.project.screens_cus.Urlaubsanspruch
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.ZustandManager
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.example.project.utils.UserAvatarCircle
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date

private val buchungsklaerungJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun buchungsklaerungApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private object BuchungsklaerungApi {
    private fun firmenId(): String =
        LoginService.getUser()?.firmenId.orEmpty().trim()
            .ifBlank { GlobalManager.currentFirmenId.trim() }

    private fun markZeitChanged(userId: String, datum: String = "") {
        val userPart = userId.trim().ifBlank { "~" }
        val filePart = datum.trim().ifBlank { "tag" }
        GlobalManager.markJsonChanged("zeit", "user/$userPart/zeiterfassung/$filePart.json")
    }

    private fun markUrlaubChanged(userId: String) {
        val userPart = userId.trim().ifBlank { "~" }
        GlobalManager.markJsonChanged("urlaub", "user/$userPart/termine/urlaub.json")
    }

    suspend fun loadMonth(month: BuchungsklaerungMonth): List<All> {
        val raw = runCatching {
            LumaticaApiManager.call(
                "personalkontingent/month-json/${buchungsklaerungApiPart(firmenId())}/${month.year}/${month.month}"
            )
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { buchungsklaerungJson.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }
            .getOrDefault(emptyList())
    }

    suspend fun loadYear(year: Int): List<All> {
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/year-json/${buchungsklaerungApiPart(firmenId())}/$year")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { buchungsklaerungJson.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }
            .getOrDefault(emptyList())
    }

    suspend fun loadProfiles(): ZeitprofilConfig {
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/profile-json/${buchungsklaerungApiPart(firmenId())}")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return ZeitprofilConfig(profile = listOf(defaultZeitprofil()))
        return runCatching { buchungsklaerungJson.decodeFromString(ZeitprofilConfig.serializer(), raw) }
            .getOrDefault(ZeitprofilConfig(profile = listOf(defaultZeitprofil())))
            .let { if (it.profile.isEmpty()) it.copy(profile = listOf(defaultZeitprofil())) else it }
    }

    suspend fun saveManualDay(
        userId: String,
        datum: String,
        von: String,
        bis: String,
        overwrite: Boolean,
        buchungsart: String
    ): All? {
        val body = buildJsonObject {
            put("userId", JsonPrimitive(userId))
            put("datum", JsonPrimitive(datum))
            put("von", JsonPrimitive(von))
            put("bis", JsonPrimitive(bis))
            put("overwrite", JsonPrimitive(overwrite))
            put("buchungsart", JsonPrimitive(buchungsart))
        }.toString()
        val raw = runCatching {
            LumaticaApiManager.postJson(
                "personalkontingent/manual-day-save-json/${buchungsklaerungApiPart(firmenId())}",
                body
            )
        }.getOrDefault("")
        if (!raw.contains("\"ok\":true") && !raw.contains("\"ok\": true")) return null
        val saved = runCatching {
            val tag = buchungsklaerungJson.parseToJsonElement(raw).jsonObject["tag"] ?: return@runCatching null
            buchungsklaerungJson.decodeFromJsonElement(All.AllNames.serializer(), tag)
        }.getOrNull()
        if (saved != null) {
            markZeitChanged(userId, datum)
            if (overwrite) markUrlaubChanged(userId)
        }
        return saved
    }

    suspend fun saveAbsenceDay(
        userId: String,
        datum: String,
        abwesenheitsart: String,
        overwrite: Boolean,
        vorgangsart: String,
        existingId: String
    ): All? {
        val body = buildJsonObject {
            put("userId", JsonPrimitive(userId))
            put("datumVon", JsonPrimitive(datum))
            put("datumBis", JsonPrimitive(datum))
            put("abwesenheitsart", JsonPrimitive(abwesenheitsart))
            put("vorgangsart", JsonPrimitive(vorgangsart))
            if (existingId.isNotBlank()) put("id", JsonPrimitive(existingId))
            put(
                "zustand",
                JsonPrimitive(
                    if (ZustandManager.Abwesenheit.isKrankheit(abwesenheitsart)) {
                        ZustandManager.Abwesenheit.ERFASST
                    } else {
                        ZustandManager.Urlaub.GENEHMIGT
                    }
                )
            )
        }.toString()
        val endpoint = if (overwrite) "replace-day-json" else "save-json"
        val raw = runCatching {
            LumaticaApiManager.postJson(
                "personalkontingent/$endpoint/${buchungsklaerungApiPart(firmenId())}",
                body
            )
        }.getOrDefault("")
        if (!raw.contains("\"ok\":true") && !raw.contains("\"ok\": true")) return null
        val saved = runCatching {
            val urlaub = buchungsklaerungJson.parseToJsonElement(raw).jsonObject["urlaub"] ?: return@runCatching null
            buchungsklaerungJson.decodeFromJsonElement(All.AllNames.serializer(), urlaub)
        }.getOrNull()
        if (saved != null) {
            markUrlaubChanged(userId)
            if (overwrite) markZeitChanged(userId, datum)
        }
        return saved
    }

    suspend fun saveSeries(
        userId: String,
        dates: List<String>,
        bookingType: String,
        von: String,
        bis: String
    ): BuchungsklaerungSeriesResult {
        val body = buildJsonObject {
            put("userId", JsonPrimitive(userId))
            put("dates", JsonArray(dates.map(::JsonPrimitive)))
            put("bookingType", JsonPrimitive(bookingType))
            put("von", JsonPrimitive(von))
            put("bis", JsonPrimitive(bis))
        }.toString()
        val raw = runCatching {
            LumaticaApiManager.postJson(
                "personalkontingent/series-booking-json/${buchungsklaerungApiPart(firmenId())}",
                body
            )
        }.getOrDefault("")
        if (raw.contains("\"ok\":true") || raw.contains("\"ok\": true")) {
            markZeitChanged(userId)
            markUrlaubChanged(userId)
            return BuchungsklaerungSeriesResult(ok = true)
        }
        val response = runCatching { buchungsklaerungJson.parseToJsonElement(raw).jsonObject }.getOrNull()
        val error = response?.get("error")?.jsonPrimitive?.content.orEmpty()
        val invalidDates = runCatching {
            response?.get("nichtBuchbareTage")?.jsonArray
                ?.map { it.jsonPrimitive.content }
                .orEmpty()
        }.getOrDefault(emptyList())
        val message = when {
            invalidDates.isNotEmpty() ->
                "Kein Soll-Arbeitstag: ${invalidDates.joinToString(", ") { buchungsklaerungFormatDate(it) }}. Bitte abwählen."
            error == "zeitkonto_guthaben_zu_niedrig" ->
                "Das verfügbare Stundenkonto reicht für diese Auswahl nicht aus."
            error == "urlaub_anspruch_ueberschritten" ->
                "Der verfügbare Urlaubsanspruch reicht für diese Auswahl nicht aus."
            else -> "Die Reihenbuchung konnte nicht vollständig gespeichert werden."
        }
        return BuchungsklaerungSeriesResult(ok = false, message = message)
    }

    suspend fun deleteDay(userId: String, datum: String): Boolean {
        val body = buildJsonObject {
            put("userId", JsonPrimitive(userId))
            put("datum", JsonPrimitive(datum))
        }.toString()
        val raw = runCatching {
            LumaticaApiManager.postJson(
                "personalkontingent/delete-day-json/${buchungsklaerungApiPart(firmenId())}",
                body
            )
        }.getOrDefault("")
        val saved = raw.contains("\"ok\":true") || raw.contains("\"ok\": true")
        if (saved) {
            markZeitChanged(userId, datum)
            markUrlaubChanged(userId)
        }
        return saved
    }
}

private data class BuchungsklaerungMonth(val year: Int, val month: Int) {
    fun shift(delta: Int): BuchungsklaerungMonth {
        val next = Date(year, month - 1 + delta, 1)
        return BuchungsklaerungMonth(next.getFullYear().toInt(), next.getMonth().toInt() + 1)
    }
}

private data class BuchungsklaerungDayState(
    val label: String = "",
    val colorHex: String? = null
)

private data class BuchungsklaerungIssue(
    val title: String,
    val rows: List<Pair<String, String>>
)

private fun buchungsklaerungIssueRows(
    issue: BuchungsklaerungIssue?
): List<Pair<String, String>> =
    issue?.let { listOf("Kl\u00e4rung" to it.title) + it.rows }.orEmpty()

private data class BuchungsklaerungPopupState(
    val userId: String,
    val iso: String
)

private data class BuchungsklaerungSavedBooking(
    val userId: String,
    val iso: String,
    val bookingType: String,
    val absenceType: String = "",
    val von: String = "",
    val bis: String = "",
    val overwrite: Boolean = false,
    val timeDay: All? = null,
    val absence: All? = null
)

private data class BuchungsklaerungSeriesResult(
    val ok: Boolean,
    val message: String = ""
)

@Composable
fun CUSBuchungsklaerung(art: String = "CUS") {
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("zeit"),
        GlobalManager.jsonDomainVersion("urlaub"),
        GlobalManager.jsonDomainVersion("kalender"),
        GlobalManager.jsonDomainVersion("user"),
        GlobalManager.jsonDomainVersion("personal")
    )
    val ignoredArt = art
    val scope = rememberCoroutineScope()
    var users by remember { mutableStateOf<List<All>>(emptyList()) }
    var vacations by remember { mutableStateOf<List<All>>(emptyList()) }
    var month by remember { mutableStateOf(currentBuchungsklaerungMonth()) }
    var mode by remember { mutableStateOf("Team") }
    var selectedUserId by remember { mutableStateOf("") }
    var popup by remember { mutableStateOf<BuchungsklaerungPopupState?>(null) }
    var loading by remember { mutableStateOf(true) }
    var timeDays by remember { mutableStateOf<List<All>>(emptyList()) }
    var allowancesByUser by remember { mutableStateOf<Map<String, Urlaubsanspruch>>(emptyMap()) }
    var profileConfig by remember { mutableStateOf(ZeitprofilConfig(profile = listOf(defaultZeitprofil()))) }
    var groupMode by remember { mutableStateOf(false) }
    var selectedGroupDays by remember { mutableStateOf<Set<String>>(emptySet()) }
    var showGroupDialog by remember { mutableStateOf(false) }

    suspend fun reload(showLoading: Boolean = true) {
        if (showLoading) loading = true
        users = UrlaubsApi.loadUsers().sortedWith(
            compareBy<All> { it.personalnummer.ifBlank { "\uFFFF" } }
                .thenBy { buchungsklaerungUserName(it) }
        )
        vacations = if (mode == "Einzel") {
            UrlaubsApi.loadYear(month.year)
        } else {
            UrlaubsApi.loadMonth(month.year, month.month)
        }
        allowancesByUser = UrlaubsApi.loadAllowances(month.year).associateBy { it.userId }
        profileConfig = BuchungsklaerungApi.loadProfiles()
        timeDays = if (mode == "Einzel") {
            BuchungsklaerungApi.loadYear(month.year)
        } else {
            BuchungsklaerungApi.loadMonth(month)
        }
        loading = false
    }

    fun applySavedBooking(saved: BuchungsklaerungSavedBooking) {
        val sameDay: (All) -> Boolean = { item ->
            val itemUserId = item.userId.ifBlank { item.kontakt }
            itemUserId == saved.userId && (
                item.datum == saved.iso ||
                    saved.iso in buchungsklaerungAbsenceDays(item)
                )
        }
        if (saved.bookingType == "Tag löschen") {
            timeDays = timeDays.filterNot(sameDay)
            vacations = vacations.filterNot(sameDay)
        } else if (saved.bookingType == "Arbeitstag" || saved.bookingType == "Homeoffice") {
            val localDay = saved.timeDay ?: All(
                userId = saved.userId,
                datum = saved.iso,
                art = saved.bookingType,
                bezeichnung = saved.bookingType,
                status = "gebucht",
                stempelungen = listOf(
                    All(typ = "einstempeln", uhrzeitVon = saved.von),
                    All(typ = "ausstempeln", uhrzeitVon = saved.bis)
                ),
                sessions = listOf(All(uhrzeitVon = saved.von, uhrzeitBis = saved.bis))
            )
            timeDays = timeDays.filterNot(sameDay) + localDay
            if (saved.overwrite) vacations = vacations.filterNot(sameDay)
        } else {
            val localAbsence = saved.absence ?: All(
                id = "local-${saved.userId}-${saved.iso}",
                userId = saved.userId,
                kontakt = saved.userId,
                datum = saved.iso,
                datumVon = saved.iso,
                datumBis = saved.iso,
                tage = listOf(saved.iso),
                art = saved.absenceType,
                abwesenheitsart = saved.absenceType,
                bezeichnung = saved.absenceType,
                zustand = if (ZustandManager.Abwesenheit.isKrankheit(saved.absenceType)) {
                    ZustandManager.Abwesenheit.ERFASST
                } else {
                    ZustandManager.Urlaub.GENEHMIGT
                }
            )
            timeDays = timeDays.filterNot(sameDay)
            vacations = vacations.filterNot(sameDay) + localAbsence
        }
    }

    LaunchedEffect(ignoredArt, month, mode, refreshVersion) { reload() }
    LaunchedEffect(users) {
        if (selectedUserId.isBlank() || users.none { it.id == selectedUserId }) {
            selectedUserId = users.firstOrNull()?.id.orEmpty()
        }
    }
    LaunchedEffect(mode, selectedUserId, month.year) {
        groupMode = false
        selectedGroupDays = emptySet()
        showGroupDialog = false
    }

    Style(BuchungsklaerungStyles)
    Div({ classes(BuchungsklaerungStyles.page) }) {
        Div({ classes(BuchungsklaerungStyles.toolbar) }) {
            Div({ classes(BuchungsklaerungStyles.toolbarLeft) }) {
                Div({ classes(BuchungsklaerungStyles.modeSwitch) }) {
                    LumaticaButton(
                        label = "Teamansicht",
                        onClick = { mode = "Team" },
                        variant = LumaticaButtonVariant.State,
                        isActive = mode == "Team",
                        heightPx = 32
                    )
                    LumaticaButton(
                        label = "Einzelansicht",
                        onClick = { mode = "Einzel" },
                        variant = LumaticaButtonVariant.State,
                        isActive = mode == "Einzel",
                        heightPx = 32
                    )
                }
            }
            Div({ classes(BuchungsklaerungStyles.toolbarCenter) }) {
                if (mode == "Einzel") {
                    Div({ classes(BuchungsklaerungStyles.userDropdown) }) {
                        AppDropdown(
                            options = users.map { All(key = it.id, label = buchungsklaerungUserName(it)) },
                            selectedValue = selectedUserId.ifBlank { null },
                            onChange = {
                                selectedUserId = it
                                popup = null
                            },
                            placeholder = "Mitarbeiter w\u00e4hlen",
                            appearance = AppDropdownAppearance.Surface
                        )
                    }
                }
                BuchungsklaerungNavButton("images/left.svg", "Zurück") {
                    month = if (mode == "Einzel") {
                        BuchungsklaerungMonth(month.year - 1, month.month)
                    } else {
                        month.shift(-1)
                    }
                }
                Span({ classes(BuchungsklaerungStyles.monthLabel) }) {
                    Text(if (mode == "Einzel") month.year.toString() else "${buchungsklaerungMonthName(month.month)} ${month.year}")
                }
                BuchungsklaerungNavButton("images/right.svg", "Weiter") {
                    month = if (mode == "Einzel") {
                        BuchungsklaerungMonth(month.year + 1, month.month)
                    } else {
                        month.shift(1)
                    }
                }
            }
            Div({ classes(BuchungsklaerungStyles.toolbarRight) }) {
                if (mode == "Einzel") {
                    if (groupMode) {
                        Span({ classes(BuchungsklaerungStyles.groupCount) }) {
                            Text("${selectedGroupDays.size} markiert")
                        }
                        LumaticaButton(
                            label = "Auswahl buchen",
                            onClick = { showGroupDialog = selectedGroupDays.isNotEmpty() },
                            enabled = selectedGroupDays.isNotEmpty(),
                            variant = LumaticaButtonVariant.Action,
                            heightPx = 32
                        )
                        LumaticaButton(
                            label = "Abbrechen",
                            onClick = {
                                groupMode = false
                                selectedGroupDays = emptySet()
                            },
                            heightPx = 32
                        )
                    } else {
                        LumaticaButton(
                            label = "Gruppenkorrektur",
                            onClick = {
                                popup = null
                                groupMode = true
                                selectedGroupDays = emptySet()
                            },
                            variant = LumaticaButtonVariant.Action,
                            heightPx = 32
                        )
                    }
                }
            }
        }

        if (mode == "Einzel") {
            PersonalKontingentStrip(allowancesByUser[selectedUserId])
        }

        if (loading) {
            Div({ classes(BuchungsklaerungStyles.loading) }) { Text("Buchungen werden gepr\u00fcft.") }
        } else if (mode == "Team") {
            BuchungsklaerungTeamMonth(
                users = users,
                vacations = vacations,
                timeDays = timeDays,
                month = month,
                profileConfig = profileConfig,
                onOpenDay = { userId, iso -> popup = BuchungsklaerungPopupState(userId, iso) }
            )
        } else {
            BuchungsklaerungSingleTimeline(
                user = users.firstOrNull { it.id == selectedUserId },
                vacations = vacations,
                timeDays = timeDays,
                anchorMonth = month,
                profileConfig = profileConfig,
                selectionMode = groupMode,
                selectedDays = selectedGroupDays,
                onOpenDay = { iso ->
                    if (groupMode) {
                        selectedGroupDays = if (iso in selectedGroupDays) {
                            selectedGroupDays - iso
                        } else {
                            selectedGroupDays + iso
                        }
                    } else {
                        popup = BuchungsklaerungPopupState(selectedUserId, iso)
                    }
                }
            )
        }

        popup?.let { state ->
            BuchungsklaerungDayPopup(
                state = state,
                users = users,
                timeDays = timeDays,
                vacations = vacations,
                profileConfig = profileConfig,
                onClose = { popup = null },
                onShiftDate = { delta -> popup = state.copy(iso = buchungsklaerungShiftIso(state.iso, delta)) },
                onSaved = { saved ->
                    applySavedBooking(saved)
                    popup = null
                    scope.launch {
                        reload(showLoading = false)
                    }
                }
            )
        }
        if (showGroupDialog) {
            BuchungsklaerungGroupDialog(
                userId = selectedUserId,
                dates = selectedGroupDays.toList().sorted(),
                profileConfig = profileConfig,
                onClose = { showGroupDialog = false },
                onSaved = {
                    showGroupDialog = false
                    groupMode = false
                    selectedGroupDays = emptySet()
                    scope.launch { reload(showLoading = false) }
                }
            )
        }
    }
}

@Composable
private fun BuchungsklaerungTeamMonth(
    users: List<All>,
    vacations: List<All>,
    timeDays: List<All>,
    month: BuchungsklaerungMonth,
    profileConfig: ZeitprofilConfig,
    onOpenDay: (String, String) -> Unit
) {
    val days = buchungsklaerungDaysInMonth(month.year, month.month)
    val prefix = "${month.year}-${month.month.toString().padStart(2, '0')}-"
    val columns = "minmax(260px, 320px) repeat($days, minmax(42px, 1fr))"
    Div({ classes(BuchungsklaerungStyles.tableScroll) }) {
        Div({
            classes(BuchungsklaerungStyles.table)
            style {
                property("grid-template-columns", columns)
                minWidth((260 + days * 42).px)
            }
        }) {
            Div({ classes(BuchungsklaerungStyles.identityHeader) }) {
                Span { Text("Mitarbeiter") }
                Span({ classes(BuchungsklaerungStyles.identityHeaderMeta) }) { Text("Fehlbuchungen") }
            }
            (1..days).forEach { day ->
                val date = Date(month.year, month.month - 1, day)
                val iso = "$prefix${day.toString().padStart(2, '0')}"
                val isToday = iso == buchungsklaerungTodayIso()
                val isWeekend = date.getDay().toInt() in setOf(0, 6)
                Div({
                    classes(BuchungsklaerungStyles.dayHeader)
                    if (isWeekend) classes(BuchungsklaerungStyles.weekend)
                    if (isToday) style { property("box-shadow", "inset 0 0 0 2px ${UISettings.ACCENT_HEX}") }
                }) {
                    Span({ classes(BuchungsklaerungStyles.weekday) }) { Text(buchungsklaerungWeekday(date.getDay().toInt())) }
                    Span({
                        classes(BuchungsklaerungStyles.dayNumber)
                        if (isToday) style { color(UISettings.ACCENT) }
                    }) { Text(day.toString()) }
                }
            }
            users.forEach { user ->
                val issueCount = (1..days).count { day ->
                    val iso = "$prefix${day.toString().padStart(2, '0')}"
                    buchungsklaerungIssueFor(user.id, iso, timeDays, vacations, profileConfig) != null
                }
                Div({ classes(BuchungsklaerungStyles.identityCell) }) {
                    UserAvatarCircle(user = user, fallbackLabel = buchungsklaerungUserName(user), sizePx = 34)
                    Span({ classes(BuchungsklaerungStyles.userName) }) { Text(buchungsklaerungUserName(user)) }
                    Span({
                        classes(BuchungsklaerungStyles.issueBalance)
                        if (issueCount > 0) classes(BuchungsklaerungStyles.issueBalanceActive)
                    }) { Text(issueCount.toString()) }
                }
                (1..days).forEach { day ->
                    val iso = "$prefix${day.toString().padStart(2, '0')}"
                    val record = buchungsklaerungRecordFor(user.id, iso, timeDays)
                    val issue = buchungsklaerungIssueFor(user.id, iso, timeDays, vacations, profileConfig)
                    BuchungsklaerungDayCell(
                        state = buchungsklaerungDayState(user.id, iso, vacations),
                        isToday = iso == buchungsklaerungTodayIso(),
                        isWeekend = buchungsklaerungParseDate(iso).getDay().toInt() in setOf(0, 6),
                        hasBooking = record != null,
                        issue = issue,
                        hoverRows = kontingentDayRows(user.id, iso, record, vacations, profileConfig) +
                            buchungsklaerungIssueRows(issue),
                        onClick = { onOpenDay(user.id, iso) }
                    )
                }
            }
        }
    }
}

@Composable
private fun BuchungsklaerungSingleTimeline(
    user: All?,
    vacations: List<All>,
    timeDays: List<All>,
    anchorMonth: BuchungsklaerungMonth,
    profileConfig: ZeitprofilConfig,
    selectionMode: Boolean,
    selectedDays: Set<String>,
    onOpenDay: (String) -> Unit
) {
    if (user == null) {
        Div({ classes(BuchungsklaerungStyles.loading) }) { Text("Kein Mitarbeiter ausgewählt.") }
        return
    }
    val months = (1..12).map { BuchungsklaerungMonth(anchorMonth.year, it) }
    val columns = "minmax(290px, 320px) repeat(31, minmax(42px, 1fr))"
    Div({ classes(BuchungsklaerungStyles.tableScroll) }) {
        Div({
            classes(BuchungsklaerungStyles.table, BuchungsklaerungStyles.timelineTable)
            style {
                property("grid-template-columns", columns)
                minWidth((290 + 31 * 42).px)
            }
        }) {
            months.forEach { rowMonth ->
                val rowPrefix = "${rowMonth.year}-${rowMonth.month.toString().padStart(2, '0')}-"
                val daysInMonth = buchungsklaerungDaysInMonth(rowMonth.year, rowMonth.month)
                val issueCount = (1..daysInMonth).count { day ->
                    val iso = "$rowPrefix${day.toString().padStart(2, '0')}"
                    buchungsklaerungIssueFor(user.id, iso, timeDays, vacations, profileConfig) != null
                }
                val absenceCount = (1..daysInMonth).count { day ->
                    val iso = "$rowPrefix${day.toString().padStart(2, '0')}"
                    kontingentAbsenceFor(user.id, iso, vacations)
                        ?.let(::kontingentAbsenceIsEffective) == true
                }
                val monthValues = zeitHourSummaryValues(
                    user.id,
                    timeDays,
                    vacations,
                    ZeitMonth(rowMonth.year, rowMonth.month),
                    profileConfig
                )
                val isCurrentMonth = rowMonth.year == Date().getFullYear().toInt() &&
                    rowMonth.month == Date().getMonth().toInt() + 1
                Div({
                    classes(BuchungsklaerungStyles.identityCell, BuchungsklaerungStyles.timelineIdentityCell)
                }) {
                    Div({ classes(BuchungsklaerungStyles.monthIdentityText) }) {
                        Span({
                            classes(BuchungsklaerungStyles.userName)
                            if (isCurrentMonth) classes(BuchungsklaerungStyles.currentMonthName)
                        }) { Text(buchungsklaerungMonthShortName(rowMonth.month)) }
                        Div({ classes(BuchungsklaerungStyles.monthSummary) }) {
                            Div({ classes(BuchungsklaerungStyles.monthMetric) }) {
                                Span({ classes(BuchungsklaerungStyles.monthMetricLabel) }) {
                                    Text("Abwesenheit")
                                }
                                Span({ classes(BuchungsklaerungStyles.monthMetricValue) }) {
                                    Text(absenceCount.toString())
                                }
                            }
                            Div({ classes(BuchungsklaerungStyles.monthMetric) }) {
                                Span({ classes(BuchungsklaerungStyles.monthMetricLabel) }) {
                                    Text("Erreichte Stunden")
                                }
                                Span({ classes(BuchungsklaerungStyles.monthMetricValue) }) {
                                    Text(zeitFormatHours(monthValues.first))
                                }
                            }
                            Div({ classes(BuchungsklaerungStyles.monthMetric) }) {
                                Span({ classes(BuchungsklaerungStyles.monthMetricLabel) }) {
                                    Text("Fehler")
                                }
                                Span({
                                    classes(BuchungsklaerungStyles.monthMetricValue)
                                    if (issueCount > 0) classes(BuchungsklaerungStyles.issueBalanceActive)
                                }) { Text(issueCount.toString()) }
                            }
                        }
                    }
                }
                (1..31).forEach { day ->
                    if (day > daysInMonth) {
                        Div({ classes(BuchungsklaerungStyles.emptyDayCell) }) {}
                    } else {
                        val iso = "$rowPrefix${day.toString().padStart(2, '0')}"
                        val record = buchungsklaerungRecordFor(user.id, iso, timeDays)
                        val issue = buchungsklaerungIssueFor(user.id, iso, timeDays, vacations, profileConfig)
                        BuchungsklaerungTimelineDayCell(
                            state = buchungsklaerungDayState(user.id, iso, vacations),
                            iso = iso,
                            hasBooking = record != null,
                            issue = issue,
                            selected = iso in selectedDays,
                            selectionMode = selectionMode,
                            hoverRows = kontingentDayRows(user.id, iso, record, vacations, profileConfig) +
                                buchungsklaerungIssueRows(issue),
                            onClick = { onOpenDay(iso) }
                        )
                    }
                }
            }
            val annualAbsenceCount = months.sumOf { rowMonth ->
                val prefix = "${rowMonth.year}-${rowMonth.month.toString().padStart(2, '0')}-"
                (1..buchungsklaerungDaysInMonth(rowMonth.year, rowMonth.month)).count { day ->
                    val iso = "$prefix${day.toString().padStart(2, '0')}"
                    kontingentAbsenceFor(user.id, iso, vacations)
                        ?.let(::kontingentAbsenceIsEffective) == true
                }
            }
            val annualReachedSeconds = months.sumOf { rowMonth ->
                zeitHourSummaryValues(
                    user.id,
                    timeDays,
                    vacations,
                    ZeitMonth(rowMonth.year, rowMonth.month),
                    profileConfig
                ).first
            }
            val annualIssueCount = months.sumOf { rowMonth ->
                val prefix = "${rowMonth.year}-${rowMonth.month.toString().padStart(2, '0')}-"
                (1..buchungsklaerungDaysInMonth(rowMonth.year, rowMonth.month)).count { day ->
                    val iso = "$prefix${day.toString().padStart(2, '0')}"
                    buchungsklaerungIssueFor(user.id, iso, timeDays, vacations, profileConfig) != null
                }
            }
            Div({
                classes(
                    BuchungsklaerungStyles.identityCell,
                    BuchungsklaerungStyles.timelineIdentityCell,
                    BuchungsklaerungStyles.yearSummaryCell
                )
            }) {
                Div({ classes(BuchungsklaerungStyles.yearSummaryContent) }) {
                    Span({ classes(BuchungsklaerungStyles.yearSummaryLabel) }) {
                        Text("Jahres\u00fcbersicht")
                    }
                    Div({ classes(BuchungsklaerungStyles.monthSummary) }) {
                        BuchungsklaerungSummaryMetric("Abwesenheit", annualAbsenceCount.toString())
                        BuchungsklaerungSummaryMetric(
                            "Erreichte Stunden",
                            zeitFormatHours(annualReachedSeconds)
                        )
                        BuchungsklaerungSummaryMetric(
                            "Fehler",
                            annualIssueCount.toString(),
                            isError = annualIssueCount > 0
                        )
                    }
                }
            }
            Div({
                style {
                    property("grid-column", "2 / -1")
                    minWidth(0.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                }
            }) {
                PersonalKontingentLegend(includeTimeStates = true, singleLine = true)
            }
        }
    }
}

@Composable
private fun BuchungsklaerungSummaryMetric(
    label: String,
    value: String,
    isError: Boolean = false
) {
    Div({ classes(BuchungsklaerungStyles.monthMetric) }) {
        Span({ classes(BuchungsklaerungStyles.monthMetricLabel) }) { Text(label) }
        Span({
            classes(BuchungsklaerungStyles.monthMetricValue)
            if (isError) classes(BuchungsklaerungStyles.issueBalanceActive)
        }) { Text(value) }
    }
}

@Composable
private fun BuchungsklaerungTimelineDayCell(
    state: BuchungsklaerungDayState,
    iso: String,
    hasBooking: Boolean,
    issue: BuchungsklaerungIssue?,
    selected: Boolean,
    selectionMode: Boolean,
    hoverRows: List<Pair<String, String>>,
    onClick: () -> Unit
) {
    val date = buchungsklaerungParseDate(iso)
    val nonWorkday = date.getDay().toInt() == 0 || buchungsklaerungIsHoliday(iso)
    val markerColor = when {
        state.colorHex != null -> state.colorHex
        issue != null -> ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEHLBUCHUNG)
        hasBooking -> ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.SOLL_ERFUELLT)
        else -> null
    }
    Div({
        classes(BuchungsklaerungStyles.dayCell, BuchungsklaerungStyles.timelineDayCell)
        if (selectionMode) classes(BuchungsklaerungStyles.selectableDayCell)
        if (selected) classes(BuchungsklaerungStyles.selectedDayCell)
        if (nonWorkday) classes(BuchungsklaerungStyles.nonWorkdayText)
        title(issue?.title ?: state.label.ifBlank { if (hasBooking) "Zeitbuchung" else "Keine Zeitbuchung" })
        if (markerColor != null) style { backgroundColor(Color(markerColor)) }
        onClick { onClick() }
    }) {
        Div({ classes(BuchungsklaerungStyles.timelineDayContent) }) {
            Span({ classes(BuchungsklaerungStyles.timelineCellWeekday) }) {
                Text(buchungsklaerungWeekday(date.getDay().toInt()))
            }
            Span({ classes(BuchungsklaerungStyles.timelineDayNumber) }) {
                Text(date.getDate().toInt().toString())
            }
            if (issue != null) {
                Span({ classes(BuchungsklaerungStyles.timelineWarning) }) { Text("!") }
            }
        }
        if (!selectionMode) BuchungsklaerungHover(hoverRows)
    }
}

@Composable
private fun BuchungsklaerungDayCell(
    state: BuchungsklaerungDayState,
    isToday: Boolean,
    isWeekend: Boolean,
    hasBooking: Boolean,
    issue: BuchungsklaerungIssue?,
    hoverRows: List<Pair<String, String>>,
    onClick: () -> Unit
) {
    val cellColor = when {
        state.colorHex != null -> state.colorHex
        issue != null -> ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEHLBUCHUNG)
        hasBooking -> ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.SOLL_ERFUELLT)
        else -> null
    }
    Div({
        classes(BuchungsklaerungStyles.dayCell)
        if (isWeekend) classes(BuchungsklaerungStyles.weekend)
        attr("title", issue?.title ?: state.label)
        if (cellColor != null) style { backgroundColor(Color(cellColor)) }
        if (isToday) style { property("box-shadow", "inset 0 0 0 2px ${UISettings.ACCENT_HEX}") }
        onClick { onClick() }
    }) {
        if (issue != null) {
            Span({
                classes(BuchungsklaerungStyles.inlineWarning)
                style { color(Color.white) }
            }) { Text("!") }
        }
        BuchungsklaerungHover(hoverRows)
    }
}

@Composable
private fun BuchungsklaerungHover(rows: List<Pair<String, String>>) {
    if (rows.isNotEmpty()) {
        Div({ classes(BuchungsklaerungStyles.cellHoverCard) }) {
            rows.forEach { row ->
                Div({ classes(BuchungsklaerungStyles.cellHoverRow) }) {
                    Span({ classes(BuchungsklaerungStyles.cellHoverLabel) }) { Text(row.first) }
                    Span({ classes(BuchungsklaerungStyles.cellHoverValue) }) { Text(row.second) }
                }
            }
        }
    }
}

@Composable
private fun BuchungsklaerungDayPopup(
    state: BuchungsklaerungPopupState,
    users: List<All>,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig,
    onClose: () -> Unit,
    onShiftDate: (Int) -> Unit,
    onSaved: (BuchungsklaerungSavedBooking) -> Unit
) {
    val scope = rememberCoroutineScope()
    val record = buchungsklaerungRecordFor(state.userId, state.iso, timeDays)
    val absence = buchungsklaerungAbsenceFor(state.userId, state.iso, vacations)
    val issue = buchungsklaerungIssueFor(state.userId, state.iso, timeDays, vacations, profileConfig)
    val hasIncompleteBooking = record != null && buchungsklaerungBookingIssue(record) != null
    var showBookingDialog by remember(state) { mutableStateOf(false) }
    var overwriteBooking by remember(state) { mutableStateOf(record == null) }
    var bookingType by remember(state) { mutableStateOf("Arbeitstag") }
    val defaultTimes = remember(state, profileConfig) {
        buchungsklaerungDefaultWorkdayTimes(state.userId, profileConfig)
    }
    var workStart by remember(state, defaultTimes) { mutableStateOf(defaultTimes.first) }
    var workEnd by remember(state, defaultTimes) { mutableStateOf(defaultTimes.second) }
    var saving by remember(state) { mutableStateOf(false) }
    var message by remember(state) { mutableStateOf("") }
    val referenceRows = kontingentDayRows(state.userId, state.iso, record, vacations, profileConfig)
    val detailRows = referenceRows + buchungsklaerungIssueRows(issue)
    val rows = detailRows
    val actionLabel = when {
        record == null && absence == null -> "Erfassen"
        hasIncompleteBooking -> "Erweitern"
        else -> "Editieren"
    }
    Div({ classes(BuchungsklaerungStyles.popupOverlay) }) {
        Div({ classes(BuchungsklaerungStyles.popupBox) }) {
            Div({ classes(BuchungsklaerungStyles.popupHeader) }) {
                Div({ classes(BuchungsklaerungStyles.popupActions) }) {
                    if (record == null) {
                        LumaticaButton(
                            label = actionLabel,
                            onClick = {
                                overwriteBooking = true
                                bookingType = "Arbeitstag"
                                showBookingDialog = true
                                message = ""
                            },
                            variant = LumaticaButtonVariant.Action,
                            heightPx = 32
                        )
                    } else {
                        if (hasIncompleteBooking) {
                            LumaticaButton(
                                label = actionLabel,
                                onClick = {
                                    overwriteBooking = false
                                    bookingType = "Arbeitstag"
                                    showBookingDialog = true
                                    message = ""
                                },
                                variant = LumaticaButtonVariant.Action,
                                heightPx = 32
                            )
                        } else {
                            LumaticaButton(
                                label = actionLabel,
                                onClick = {
                                    overwriteBooking = true
                                    bookingType = "Arbeitstag"
                                    showBookingDialog = true
                                    message = ""
                                },
                                variant = LumaticaButtonVariant.Action,
                                heightPx = 32
                            )
                        }
                    }
                }
                Button(attrs = {
                    classes(BuchungsklaerungStyles.closeButton)
                    onClick { onClose() }
                }) { Text("x") }
            }
            Div({ classes(BuchungsklaerungStyles.popupNavRow) }) {
                BuchungsklaerungNavButton("images/left.svg", "Vorheriger Tag") { onShiftDate(-1) }
                H3({ classes(BuchungsklaerungStyles.popupTitle) }) { Text(buchungsklaerungCompactHeader(state.iso)) }
                BuchungsklaerungNavButton("images/right.svg", "Nächster Tag") { onShiftDate(1) }
            }
            Div({ classes(BuchungsklaerungStyles.popupTable) }) {
                rows.forEach { row ->
                    Div({ classes(BuchungsklaerungStyles.popupTableRow) }) {
                        Span({ classes(BuchungsklaerungStyles.popupTableCell) }) { Text(row.first) }
                        Span({ classes(BuchungsklaerungStyles.popupTableValue) }) { Text(row.second) }
                    }
                }
            }
            if (showBookingDialog) {
                Div({ classes(BuchungsklaerungStyles.bookingBox) }) {
                    Span({ classes(BuchungsklaerungStyles.bookingTitle) }) {
                        Text(actionLabel)
                    }
                    Div({ classes(BuchungsklaerungStyles.bookingField) }) {
                        Span({ classes(BuchungsklaerungStyles.bookingLabel) }) { Text("Was wollen Sie buchen?") }
                        AppDropdown(
                            options = buchungsklaerungBookingTypes.map { All(key = it, label = it) },
                            selectedValue = bookingType,
                            onChange = { bookingType = it },
                            appearance = AppDropdownAppearance.Surface,
                            menuMode = AppDropdownMenuMode.Overlay
                        )
                    }
                    if (bookingType == "Arbeitstag" || bookingType == "Homeoffice") {
                        Div({ classes(BuchungsklaerungStyles.bookingTimes) }) {
                            BuchungsklaerungTimeInput("Kommen", workStart, saving) { workStart = it }
                            BuchungsklaerungTimeInput("Gehen", workEnd, saving) { workEnd = it }
                        }
                    }
                    if (message.isNotBlank()) {
                        Span({ classes(BuchungsklaerungStyles.bookingMessage) }) { Text(message) }
                    }
                    Div({ classes(BuchungsklaerungStyles.bookingActions) }) {
                        LumaticaButton(
                            label = "Abbrechen",
                            onClick = { if (!saving) showBookingDialog = false },
                            enabled = !saving,
                            heightPx = 32
                        )
                        LumaticaButton(
                            label = if (saving) "Speichert..." else "Buchen",
                            onClick = {
                                if (saving) return@LumaticaButton
                                saving = true
                                message = ""
                                scope.launch {
                                    val savedTimeDay = if (bookingType == "Arbeitstag" || bookingType == "Homeoffice") {
                                        BuchungsklaerungApi.saveManualDay(
                                            userId = state.userId,
                                            datum = state.iso,
                                            von = workStart,
                                            bis = workEnd,
                                            overwrite = overwriteBooking,
                                            buchungsart = bookingType
                                        )
                                    } else {
                                        null
                                    }
                                    val deletedDay = if (bookingType == "Tag löschen") {
                                        BuchungsklaerungApi.deleteDay(state.userId, state.iso)
                                    } else {
                                        false
                                    }
                                    val savedAbsence = if (bookingType == "Arbeitstag" || bookingType == "Homeoffice") {
                                        null
                                    } else if (bookingType == "Tag löschen") {
                                        null
                                    } else {
                                        BuchungsklaerungApi.saveAbsenceDay(
                                            userId = state.userId,
                                            datum = state.iso,
                                            abwesenheitsart = buchungsklaerungAbsenceBookingType(bookingType),
                                            overwrite = overwriteBooking,
                                            vorgangsart = if (record == null && absence == null) {
                                                "Manuelle Buchung"
                                            } else {
                                                "Korrektur"
                                            },
                                            existingId = absence?.id.orEmpty()
                                        )
                                    }
                                    saving = false
                                    if (savedTimeDay != null || savedAbsence != null || deletedDay) {
                                        val absenceType = buchungsklaerungAbsenceBookingType(bookingType)
                                        onSaved(
                                            BuchungsklaerungSavedBooking(
                                                userId = state.userId,
                                                iso = state.iso,
                                                bookingType = bookingType,
                                                absenceType = absenceType,
                                                von = workStart,
                                                bis = workEnd,
                                                overwrite = overwriteBooking,
                                                timeDay = savedTimeDay,
                                                absence = savedAbsence
                                            )
                                        )
                                    } else {
                                        message = "Buchung konnte nicht gespeichert werden."
                                    }
                                }
                            },
                            enabled = !saving,
                            variant = LumaticaButtonVariant.Action,
                            heightPx = 32
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun BuchungsklaerungGroupDialog(
    userId: String,
    dates: List<String>,
    profileConfig: ZeitprofilConfig,
    onClose: () -> Unit,
    onSaved: () -> Unit
) {
    val scope = rememberCoroutineScope()
    var bookingType by remember { mutableStateOf("Arbeitstag") }
    val defaultTimes = remember(userId, profileConfig) {
        buchungsklaerungDefaultWorkdayTimes(userId, profileConfig)
    }
    var workStart by remember(defaultTimes) { mutableStateOf(defaultTimes.first) }
    var workEnd by remember(defaultTimes) { mutableStateOf(defaultTimes.second) }
    var saving by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf("") }
    Div({ classes(BuchungsklaerungStyles.popupOverlay) }) {
        Div({ classes(BuchungsklaerungStyles.popupBox) }) {
            Div({ classes(BuchungsklaerungStyles.popupHeader) }) {
                H3({ classes(BuchungsklaerungStyles.popupTitle) }) { Text("Gruppenkorrektur") }
                Button(attrs = {
                    classes(BuchungsklaerungStyles.closeButton)
                    onClick { if (!saving) onClose() }
                }) { Text("x") }
            }
            Div({ classes(BuchungsklaerungStyles.groupDialogSummary) }) {
                Span { Text("${dates.size} Tage markiert") }
                if (dates.isNotEmpty()) {
                    Span({ classes(BuchungsklaerungStyles.groupDialogDates) }) {
                        Text("${buchungsklaerungFormatDate(dates.first())} – ${buchungsklaerungFormatDate(dates.last())}")
                    }
                }
            }
            Div({ classes(BuchungsklaerungStyles.bookingField) }) {
                Span({ classes(BuchungsklaerungStyles.bookingLabel) }) { Text("Was wollen Sie buchen?") }
                AppDropdown(
                    options = buchungsklaerungBookingTypes.map { All(key = it, label = it) },
                    selectedValue = bookingType,
                    onChange = { bookingType = it },
                    appearance = AppDropdownAppearance.Surface,
                    menuMode = AppDropdownMenuMode.Overlay
                )
            }
            if (bookingType == "Arbeitstag" || bookingType == "Homeoffice") {
                Div({ classes(BuchungsklaerungStyles.bookingTimes) }) {
                    BuchungsklaerungTimeInput("Kommen", workStart, saving) { workStart = it }
                    BuchungsklaerungTimeInput("Gehen", workEnd, saving) { workEnd = it }
                }
            }
            if (message.isNotBlank()) {
                Span({ classes(BuchungsklaerungStyles.bookingMessage) }) { Text(message) }
            }
            Div({ classes(BuchungsklaerungStyles.bookingActions) }) {
                LumaticaButton(
                    label = "Abbrechen",
                    onClick = { if (!saving) onClose() },
                    enabled = !saving,
                    heightPx = 32
                )
                LumaticaButton(
                    label = if (saving) "Speichert..." else "${dates.size} Tage buchen",
                    onClick = {
                        if (saving || dates.isEmpty()) return@LumaticaButton
                        saving = true
                        message = ""
                        scope.launch {
                            val result = BuchungsklaerungApi.saveSeries(
                                userId = userId,
                                dates = dates,
                                bookingType = buchungsklaerungAbsenceBookingType(bookingType),
                                von = workStart,
                                bis = workEnd
                            )
                            saving = false
                            if (result.ok) onSaved()
                            else message = result.message
                        }
                    },
                    enabled = !saving && dates.isNotEmpty(),
                    variant = LumaticaButtonVariant.Action,
                    heightPx = 32
                )
            }
        }
    }
}

@Composable
private fun BuchungsklaerungTimeInput(
    label: String,
    value: String,
    saving: Boolean,
    onChange: (String) -> Unit
) {
    Div({ classes(BuchungsklaerungStyles.bookingField) }) {
        Span({ classes(BuchungsklaerungStyles.bookingLabel) }) { Text(label) }
        Input(InputType.Time, attrs = {
            classes(BuchungsklaerungStyles.bookingInput)
            value(value)
            if (saving) disabled()
            onInput { onChange(it.value) }
        })
    }
}

@Composable
private fun BuchungsklaerungNavButton(icon: String, tooltip: String, onClick: () -> Unit) {
    Button(attrs = {
        classes(BuchungsklaerungStyles.navButton)
        attr("title", tooltip)
        onClick { onClick() }
    }) {
        Img(src = icon, attrs = { classes(BuchungsklaerungStyles.navIcon) })
    }
}

private fun buchungsklaerungIssueFor(
    userId: String,
    iso: String,
    timeDays: List<All>,
    vacations: List<All>,
    config: ZeitprofilConfig
): BuchungsklaerungIssue? {
    if (iso > buchungsklaerungTodayIso()) return null
    val record = buchungsklaerungRecordFor(userId, iso, timeDays)
    val approvedAbsence = buchungsklaerungApprovedAbsenceFor(userId, iso, vacations)
    val worksOnDay = buchungsklaerungProfileWorksOnUser(userId, iso, config)
    if (record == null) {
        if (!worksOnDay || buchungsklaerungIsHoliday(iso) || approvedAbsence != null) return null
        if (!buchungsklaerungMissingStartBookingIsDue(userId, iso, config)) return null
        return BuchungsklaerungIssue(
            title = "Kommen nicht gebucht",
            rows = listOf(
                "Fehler" to "Kommen nicht gebucht",
                "Erwartet" to "Arbeitstag laut Zeitprofil"
            )
        )
    }
    val bookingIssue = buchungsklaerungBookingIssue(record)
    return bookingIssue?.let { message ->
        BuchungsklaerungIssue(
            title = message,
            rows = listOf("Fehler" to message) + buchungsklaerungTimelineRows(record)
        )
    }
}

private fun buchungsklaerungMissingStartBookingIsDue(userId: String, iso: String, config: ZeitprofilConfig): Boolean {
    val today = buchungsklaerungTodayIso()
    if (iso < today) return true
    if (iso > today) return false
    val start = buchungsklaerungDefaultWorkdayTimes(userId, config).first
    val startSeconds = buchungsklaerungTimeSeconds(start) ?: buchungsklaerungTimeSeconds("08:00") ?: 0
    val now = Date()
    val nowSeconds = now.getHours().toInt() * 3600 +
        now.getMinutes().toInt() * 60 +
        now.getSeconds().toInt()
    return nowSeconds >= startSeconds
}

private fun buchungsklaerungBookingIssue(day: All): String? {
    val events = day.stempelungen
        .filter { it.typ == "einstempeln" || it.typ == "ausstempeln" }
        .sortedBy { it.uhrzeitVon }
    if (events.isEmpty()) return null
    var open = false
    events.forEach { event ->
        when (event.typ) {
            "einstempeln" -> {
                if (open) return "Gehen nicht gebucht"
                open = true
            }
            "ausstempeln" -> {
                if (!open) return "Kommen nicht gebucht"
                open = false
            }
        }
    }
    return if (open) "Gehen nicht gebucht" else null
}

private fun buchungsklaerungDayState(userId: String, iso: String, vacations: List<All>): BuchungsklaerungDayState {
    if (buchungsklaerungIsHoliday(iso)) {
        return BuchungsklaerungDayState(
            label = ZustandManager.Zeit.textFor(ZustandManager.Zeit.FEIERTAG),
            colorHex = ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEIERTAG)
        )
    }
    buchungsklaerungAbsenceFor(userId, iso, vacations)?.let { absence ->
        val reference = kontingentAbsenceReference(absence, iso)
        return BuchungsklaerungDayState(
            label = reference.label,
            colorHex = reference.colorHex
        )
    }
    return BuchungsklaerungDayState()
}

private fun buchungsklaerungAbsenceFor(userId: String, iso: String, vacations: List<All>): All? =
    kontingentAbsenceFor(userId, iso, vacations)

private fun buchungsklaerungReferenceRows(
    userId: String,
    iso: String,
    vacations: List<All>
): List<Pair<String, String>> =
    buchungsklaerungAbsenceFor(userId, iso, vacations)
        ?.let { kontingentAbsenceReference(it, iso).rows }
        .orEmpty()

private fun buchungsklaerungApprovedAbsenceFor(userId: String, iso: String, vacations: List<All>): All? =
    vacations.firstOrNull {
        buchungsklaerungTerminUserId(it) == userId &&
            iso in buchungsklaerungAbsenceDays(it) &&
            kontingentAbsenceIsEffective(it)
    }

private fun buchungsklaerungTerminUserId(termin: All): String =
    termin.userId.ifBlank { termin.kontakt }

private fun buchungsklaerungAbsenceDays(termin: All): List<String> =
    termin.tage.takeIf { it.isNotEmpty() } ?: listOf(termin.datum).filter { it.isNotBlank() }

private fun buchungsklaerungAbsenceType(termin: All): String =
    kontingentAbsenceType(termin)

private fun buchungsklaerungAbsenceStatusPriority(zustand: Int): Int =
    when (zustand) {
        ZustandManager.Urlaub.GENEHMIGT -> 4
        ZustandManager.Urlaub.IN_BEARBEITUNG -> 3
        ZustandManager.Urlaub.BEANTRAGT -> 2
        else -> 1
    }

private fun buchungsklaerungRecordFor(userId: String, iso: String, timeDays: List<All>): All? =
    timeDays.firstOrNull { it.userId == userId && it.datum == iso }

private fun buchungsklaerungTimelineRows(day: All?): List<Pair<String, String>> {
    if (day == null) return emptyList()
    val rows = mutableListOf<Pair<String, String>>()
    day.stempelungen.sortedBy { it.uhrzeitVon }.forEach { event ->
        val label = when (event.typ) {
            "einstempeln" -> "Kommen"
            "ausstempeln" -> "Gehen"
            "bereichwechsel" -> "Bereich"
            else -> event.bezeichnung.ifBlank { event.typ }
        }
        val value = if (event.typ == "bereichwechsel") {
            "${buchungsklaerungFormatClock(event.uhrzeitVon)} / ${event.bereich.ifBlank { "-" }}"
        } else {
            buchungsklaerungFormatClock(event.uhrzeitVon)
        }
        rows += label to value
    }
    if (rows.isEmpty() && day.sessions.isNotEmpty()) {
        day.sessions.forEach { session ->
            rows += "Kommen" to buchungsklaerungFormatClock(session.uhrzeitVon)
            rows += "Gehen" to buchungsklaerungFormatClock(session.uhrzeitBis)
        }
    }
    return rows
}

private fun buchungsklaerungProfileWorksOnUser(userId: String, iso: String, config: ZeitprofilConfig): Boolean {
    val profile = config.profile.firstOrNull { it.id == config.zuordnungen[userId] }
    val days = profile?.arbeitstage?.takeIf { it.isNotEmpty() } ?: defaultZeitArbeitstage()
    return days[buchungsklaerungProfileDayKey(iso)] == true
}

private val buchungsklaerungBookingTypes = listOf(
    "Tag löschen",
    "Zeitausgleich",
    "Urlaub",
    "Arbeitstag",
    "Unbezahlt Frei",
    "Homeoffice",
    "Krankheit"
)

private fun buchungsklaerungAbsenceBookingType(value: String): String =
    when (value) {
        "Zeitausgleich" -> "Freizeitausgleich"
        "Unbezahlt Frei" -> "Unbezahlt frei"
        else -> value
    }

private fun buchungsklaerungDefaultWorkdayTimes(userId: String, config: ZeitprofilConfig): Pair<String, String> {
    val profile = config.profile.firstOrNull { it.id == config.zuordnungen[userId] }
    return (profile?.start?.takeIf { it.isNotBlank() } ?: "08:00") to
        (profile?.ende?.takeIf { it.isNotBlank() } ?: "17:00")
}

private fun buchungsklaerungProfileDayKey(iso: String): String =
    when (buchungsklaerungParseDate(iso).getDay().toInt()) {
        0 -> "so"
        1 -> "mo"
        2 -> "di"
        3 -> "mi"
        4 -> "do"
        5 -> "fr"
        6 -> "sa"
        else -> "mo"
    }

private fun currentBuchungsklaerungMonth(): BuchungsklaerungMonth {
    val now = Date()
    return BuchungsklaerungMonth(now.getFullYear().toInt(), now.getMonth().toInt() + 1)
}

private fun buchungsklaerungTodayIso(): String {
    val now = Date()
    return "${now.getFullYear().toInt()}-${(now.getMonth().toInt() + 1).toString().padStart(2, '0')}-${now.getDate().toInt().toString().padStart(2, '0')}"
}

private fun buchungsklaerungDaysInMonth(year: Int, month: Int): Int =
    Date(year, month, 0).getDate().toInt()

private fun buchungsklaerungParseDate(iso: String): Date {
    val parts = iso.split("-")
    return Date(
        parts.getOrNull(0)?.toIntOrNull() ?: Date().getFullYear().toInt(),
        (parts.getOrNull(1)?.toIntOrNull() ?: 1) - 1,
        parts.getOrNull(2)?.toIntOrNull() ?: 1
    )
}

private fun buchungsklaerungShiftIso(iso: String, delta: Int): String {
    val date = buchungsklaerungParseDate(iso)
    val next = Date(date.getFullYear().toInt(), date.getMonth().toInt(), date.getDate().toInt() + delta)
    return "${next.getFullYear().toInt()}-${(next.getMonth().toInt() + 1).toString().padStart(2, '0')}-${next.getDate().toInt().toString().padStart(2, '0')}"
}

private fun buchungsklaerungIsHoliday(iso: String): Boolean =
    iso.takeLast(5) in setOf("01-01", "05-01", "10-03", "12-25", "12-26")

private fun buchungsklaerungFormatDate(iso: String): String {
    val parts = iso.split("-")
    return if (parts.size == 3) "${parts[2]}.${parts[1]}.${parts[0]}" else iso
}

private fun buchungsklaerungCompactHeader(iso: String): String {
    val date = buchungsklaerungParseDate(iso)
    return "${buchungsklaerungWeekday(date.getDay().toInt()).uppercase()} ${buchungsklaerungFormatDate(iso)}"
}

private fun buchungsklaerungFormatClock(value: String): String {
    val parts = value.split(":")
    val hours = parts.getOrNull(0)?.toIntOrNull() ?: return value.ifBlank { "-" }
    val minutes = parts.getOrNull(1)?.toIntOrNull() ?: return value.ifBlank { "-" }
    return "${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}"
}

private fun buchungsklaerungTimeSeconds(value: String): Int? {
    val parts = value.split(":")
    val hours = parts.getOrNull(0)?.toIntOrNull() ?: return null
    val minutes = parts.getOrNull(1)?.toIntOrNull() ?: return null
    if (hours !in 0..23 || minutes !in 0..59) return null
    return hours * 3600 + minutes * 60
}

private fun buchungsklaerungUserName(user: All): String =
    user.name.ifBlank { listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ") }
        .ifBlank { user.email.ifBlank { user.id } }

private fun buchungsklaerungWeekday(jsDay: Int): String =
    listOf("SO", "MO", "DI", "MI", "DO", "FR", "SA").getOrElse(jsDay) { "" }

private fun buchungsklaerungMonthName(month: Int): String =
    listOf("Januar", "Februar", "M\u00e4rz", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember")
        .getOrElse(month - 1) { month.toString() }

private fun buchungsklaerungMonthShortName(month: Int): String =
    listOf("Jan.", "Feb.", "M\u00e4r.", "Apr.", "Mai", "Jun.", "Jul.", "Aug.", "Sep.", "Okt.", "Nov.", "Dez.")
        .getOrElse(month - 1) { month.toString() }

private object BuchungsklaerungStyles : StyleSheet() {
    val page by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        padding(18.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        color(Color.white)
        overflow("hidden")
    }
    val toolbar by style {
        flexShrink(0)
        display(DisplayStyle.Grid)
        property("grid-template-columns", "1fr auto 1fr")
        alignItems(AlignItems.Center)
        gap(14.px)
    }
    val toolbarLeft by style { display(DisplayStyle.Flex) }
    val toolbarCenter by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(10.px)
        flexWrap(FlexWrap.Wrap)
    }
    val toolbarRight by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexEnd)
        gap(6.px)
    }
    val groupCount by style {
        color(rgba(255, 255, 255, 0.68))
        fontSize(13.px)
        fontWeight("800")
        property("white-space", "nowrap")
    }
    val groupDialogSummary by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(12.px)
        padding(10.px, 0.px)
        fontSize(15.px)
        fontWeight("900")
    }
    val groupDialogDates by style {
        color(rgba(255, 255, 255, 0.58))
        fontSize(13.px)
        fontWeight("700")
    }
    val modeSwitch by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
    }
    val userDropdown by style { width(220.px) }
    val monthLabel by style {
        minWidth(150.px)
        textAlign("center")
        fontSize(15.px)
        fontWeight("900")
    }
    val navButton by style {
        width(32.px)
        height(32.px)
        padding(0.px)
        borderRadius(6.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        backgroundColor(rgba(255, 255, 255, 0.06))
        cursor("pointer")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }
    val navIcon by style {
        width(13.px)
        height(13.px)
        property("filter", "brightness(0) invert(1)")
    }
    val loading by style {
        padding(16.px)
        color(rgba(255, 255, 255, 0.68))
    }
    val tableScroll by style {
        minHeight(0.px)
        flexGrow(1)
        overflow("auto")
    }
    val table by style {
        width(100.percent)
        display(DisplayStyle.Grid)
        property("grid-auto-rows", "60px")
    }
    val timelineTable by style {
        property("grid-auto-rows", "72px")
    }
    val identityHeader by style {
        position(Position.Sticky)
        left(0.px)
        top(0.px)
        property("z-index", "4")
        padding(0.px, 12.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        backgroundColor(Color("#20242a"))
        property("border-right", "1px solid rgba(255,255,255,0.14)")
        property("border-bottom", "1px solid rgba(255,255,255,0.14)")
        fontSize(15.px)
        fontWeight("800")
        boxSizing("border-box")
    }
    val identityHeaderMeta by style {
        color(rgba(255, 255, 255, 0.48))
        fontSize(13.px)
        fontWeight("700")
    }
    val dayHeader by style {
        position(Position.Sticky)
        top(0.px)
        property("z-index", "3")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        backgroundColor(Color("#20242a"))
        property("border-right", "1px solid rgba(255,255,255,0.08)")
        property("border-bottom", "1px solid rgba(255,255,255,0.14)")
    }
    val weekday by style {
        color(rgba(255, 255, 255, 0.44))
        fontSize(13.px)
        fontWeight("700")
    }
    val dayNumber by style {
        fontSize(16.px)
        fontWeight("900")
    }
    val selectableDayCell by style {
        cursor("crosshair")
        property("box-shadow", "inset 0 0 0 1px rgba(255,255,255,0.2)")
    }
    val selectedDayCell by style {
        property("box-shadow", "inset 0 0 0 3px #4da3ff")
        property("filter", "brightness(1.18)")
    }
    val identityCell by style {
        position(Position.Sticky)
        left(0.px)
        property("z-index", "2")
        padding(0.px, 10.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(9.px)
        backgroundColor(Color("#191d22"))
        property("border-right", "1px solid rgba(255,255,255,0.14)")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        boxSizing("border-box")
    }
    val userName by style {
        property("flex", "1 1 auto")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
        fontSize(15.px)
        fontWeight("800")
    }
    val issueBalance by style {
        property("margin-left", "auto")
        color(rgba(255, 255, 255, 0.54))
        fontSize(15.px)
        fontWeight("900")
        property("white-space", "nowrap")
    }
    val issueBalanceActive by style {
        color(UISettings.ACCENT)
    }
    val monthIdentityText by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(10.px)
        minWidth(0.px)
    }
    val monthSummary by style {
        marginLeft(5.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(7.px)
    }
    val monthMetric by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        gap(2.px)
        property("white-space", "nowrap")
    }
    val monthMetricLabel by style {
        color(rgba(255, 255, 255, 0.48))
        fontSize(9.px)
        fontWeight("700")
    }
    val monthMetricValue by style {
        color(Color.white)
        fontSize(12.px)
        fontWeight("900")
    }
    val yearSummaryCell by style {
        position(Position.Sticky)
        left(0.px)
        minHeight(72.px)
        backgroundColor(Color("#20242a"))
    }
    val yearSummaryContent by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(5.px)
    }
    val yearSummaryLabel by style {
        color(rgba(255, 255, 255, 0.62))
        fontSize(12.px)
        fontWeight("800")
    }
    val timelineIdentityCell by style {
        padding(0.px, 7.px)
        property("border-right", "1px solid rgba(255,255,255,0.18)")
        property("border-bottom", "2px solid rgba(255,255,255,0.48)")
        property("background-clip", "padding-box")
    }
    val currentMonthName by style {
        color(UISettings.ACCENT)
    }
    val dayCell by style {
        position(Position.Relative)
        cursor("pointer")
        property("border-right", "1px solid rgba(255,255,255,0.065)")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        boxSizing("border-box")
        overflow("visible")
        property("z-index", "1")
        self + ":hover" style {
            property("box-shadow", "inset 0 0 0 2px rgba(255,255,255,0.36)")
            property("z-index", "9998")
        }
        self + ":hover > div:last-child" style {
            display(DisplayStyle.Flex)
        }
    }
    val emptyDayCell by style {
        position(Position.Relative)
        backgroundColor(rgba(255, 255, 255, 0.018))
        property("border-right", "1px solid rgba(255,255,255,0.18)")
        property("border-bottom", "2px solid rgba(255,255,255,0.48)")
        property("background-clip", "padding-box")
        boxSizing("border-box")
    }
    val timelineDayCell by style {
        property("border-right", "1px solid rgba(255,255,255,0.18)")
        property("border-bottom", "2px solid rgba(255,255,255,0.48)")
        property("background-clip", "padding-box")
    }
    val timelineDayContent by style {
        width(100.percent)
        height(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(3.px)
    }
    val timelineCellWeekday by style {
        fontSize(11.px)
        fontWeight("900")
        lineHeight("1")
        property("opacity", "0.68")
    }
    val timelineDayNumber by style {
        fontSize(15.px)
        fontWeight("900")
        lineHeight("1")
    }
    val nonWorkdayText by style {
        color(UISettings.ACCENT)
    }
    val timelineWarning by style {
        position(Position.Absolute)
        right(5.px)
        bottom(4.px)
        width(14.px)
        height(14.px)
        borderRadius(50.percent)
        backgroundColor(rgba(0, 0, 0, 0.34))
        color(Color.white)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        fontSize(10.px)
        fontWeight("900")
        lineHeight("1")
    }
    val weekend by style {
        backgroundColor(rgba(255, 255, 255, 0.035))
        color(rgba(255, 255, 255, 0.35))
    }
    val dayMarkerDot by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        width(12.px)
        height(12.px)
        borderRadius(50.percent)
        property("transform", "translate(-50%, -50%)")
        property("box-shadow", "0 0 9px currentColor")
    }
    val missingBookingDot by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        width(17.px)
        height(17.px)
        borderRadius(50.percent)
        property("transform", "translate(-50%, -50%)")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        fontSize(12.px)
        fontWeight("900")
        lineHeight("1")
        property("box-shadow", "0 0 8px currentColor")
    }
    val inlineWarning by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        property("transform", "translate(-50%, -50%)")
        fontSize(14.px)
        fontWeight("900")
        lineHeight("1")
    }
    val cellHoverCard by style {
        position(Position.Absolute)
        left(50.percent)
        top(100.percent)
        property("transform", "translateX(-50%)")
        marginTop(8.px)
        width(280.px)
        minHeight(88.px)
        maxHeight(240.px)
        display(DisplayStyle.None)
        flexDirection(FlexDirection.Column)
        gap(4.px)
        padding(12.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(6.px)
        backgroundColor(Color("#15191e"))
        property("box-shadow", "0 12px 28px rgba(0,0,0,0.42)")
        property("z-index", "9999")
        property("pointer-events", "none")
        boxSizing("border-box")
        overflow("hidden")
    }
    val cellHoverRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        gap(8.px)
        fontSize(14.px)
        lineHeight("1.25")
    }
    val cellHoverLabel by style {
        color(rgba(255, 255, 255, 0.58))
        fontWeight("800")
    }
    val cellHoverValue by style {
        color(Color.white)
        fontWeight("900")
        textAlign("right")
    }
    val popupOverlay by style {
        position(Position.Fixed)
        left(0.px)
        top(0.px)
        right(0.px)
        bottom(0.px)
        property("z-index", "10000")
        backgroundColor(rgba(0, 0, 0, 0.0))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(18.px)
        boxSizing("border-box")
        property("pointer-events", "none")
    }
    val popupBox by style {
        width(460.px)
        maxWidth(92.vw)
        maxHeight(92.vh)
        overflow("auto")
        padding(18.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        borderRadius(6.px)
        backgroundColor(Color("#191d22"))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        boxSizing("border-box")
        property("pointer-events", "auto")
    }
    val popupHeader by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(10.px)
        minHeight(32.px)
    }
    val popupActions by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
        flexWrap(FlexWrap.Wrap)
    }
    val popupNavRow by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "32px 1fr 32px")
        alignItems(AlignItems.Center)
        gap(10.px)
        minHeight(36.px)
    }
    val popupTitle by style {
        margin(0.px)
        fontSize(15.px)
        fontWeight("900")
        property("white-space", "nowrap")
        textAlign("center")
    }
    val closeButton by style {
        width(32.px)
        height(32.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(Color.white)
        cursor("pointer")
    }
    val popupTable by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "1fr 1fr")
        property("grid-auto-rows", "minmax(38px, max-content)")
        alignContent(AlignContent.Start)
        gap(0.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(5.px)
        overflow("hidden")
    }
    val popupTableRow by style {
        display(DisplayStyle.Contents)
    }
    val popupTableCell by style {
        padding(9.px, 12.px)
        color(rgba(255, 255, 255, 0.62))
        fontSize(15.px)
        fontWeight("800")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        backgroundColor(rgba(255, 255, 255, 0.03))
        property("white-space", "nowrap")
    }
    val popupTableValue by style {
        padding(9.px, 12.px)
        color(Color.white)
        fontSize(15.px)
        fontWeight("900")
        textAlign("right")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        backgroundColor(rgba(255, 255, 255, 0.02))
        property("white-space", "nowrap")
    }
    val bookingBox by style {
        padding(14.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        boxSizing("border-box")
    }
    val bookingTitle by style {
        color(Color.white)
        fontSize(13.px)
        fontWeight("900")
    }
    val bookingField by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(5.px)
    }
    val bookingLabel by style {
        color(rgba(255, 255, 255, 0.58))
        fontSize(11.px)
        fontWeight("800")
    }
    val bookingTimes by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        gap(8.px)
    }
    val bookingInput by style {
        width(100.percent)
        height(34.px)
        padding(0.px, 10.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        borderRadius(5.px)
        backgroundColor(rgba(0, 0, 0, 0.22))
        color(Color.white)
        boxSizing("border-box")
    }
    val bookingMessage by style {
        color(UISettings.ACCENT)
        fontSize(12.px)
        fontWeight("800")
    }
    val bookingActions by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        gap(8.px)
        flexWrap(FlexWrap.Wrap)
    }
}
